/**
 * Typed permission registry — single source of truth for all permission strings.
 *
 * The backing data lives in `frontend/src/lib/generated/permissions.json`, which
 * is generated from the backend seeder by running:
 *
 *   php artisan permissions:dump-frontend
 *
 * RULE: Never hard-code a permission string. Always use PERMISSIONS.X_Y so that
 * TypeScript catches typos at build time and CI (permissions:check-frontend) catches
 * seeder drift at merge time.
 *
 * Key mapping: `resource.action` → `RESOURCE_ACTION` (dots and other non-alphanum
 * chars → underscore, then uppercased). Examples:
 *   customers.view              → CUSTOMERS_VIEW
 *   orders.create_for_suspended → ORDERS_CREATE_FOR_SUSPENDED
 *   llm_usage.view_full         → LLM_USAGE_VIEW_FULL
 *   data_protection.manage      → DATA_PROTECTION_MANAGE
 */

// ---------------------------------------------------------------------------
// Canonical permission string constants
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  // Affiliates
  AFFILIATES_APPROVE: 'affiliates.approve',
  AFFILIATES_CREATE: 'affiliates.create',
  AFFILIATES_DELETE: 'affiliates.delete',
  AFFILIATES_PAYOUT: 'affiliates.payout',
  AFFILIATES_UPDATE: 'affiliates.update',
  AFFILIATES_VIEW: 'affiliates.view',

  // Audit logs
  AUDIT_LOGS_VIEW: 'audit_logs.view',

  // Billing / dunning
  BILLING_PAUSE_DUNNING: 'billing.pause_dunning',
  BILLING_RESUME_DUNNING: 'billing.resume_dunning',
  BILLING_VIEW_DUNNING: 'billing.view_dunning',

  // Reconciliation
  RECONCILIATION_VIEW: 'reconciliation.view',

  // Contracts
  CONTRACTS_MANAGE_TEMPLATES: 'contracts.manage_templates',

  // Coupons
  COUPONS_CREATE: 'coupons.create',
  COUPONS_DELETE: 'coupons.delete',
  COUPONS_UPDATE: 'coupons.update',
  COUPONS_VIEW: 'coupons.view',

  // Coverage checks
  COVERAGE_CHECKS_MANAGE: 'coverage_checks.manage',

  // Credits
  CREDITS_APPROVE: 'credits.approve',

  // Customers
  CUSTOMERS_CREATE: 'customers.create',
  CUSTOMERS_DELETE: 'customers.delete',
  CUSTOMERS_SEND_EMAIL: 'customers.send_email',
  CUSTOMERS_SEND_PORTAL_INVITE: 'customers.send_portal_invite',
  CUSTOMERS_UPDATE: 'customers.update',
  CUSTOMERS_VIEW: 'customers.view',

  // Customer request registry
  CUSTOMER_REQUESTS_CONFIRM_PRINTED_HANDOVER: 'customer_requests.confirm_printed_handover',
  CUSTOMER_REQUESTS_CREATE: 'customer_requests.create',
  CUSTOMER_REQUESTS_RETRY_RECEIPT: 'customer_requests.retry_receipt',
  CUSTOMER_REQUESTS_VIEW: 'customer_requests.view',

  // Data protection
  DATA_PROTECTION_ANONYMIZE: 'data_protection.anonymize',
  DATA_PROTECTION_EXPORT_DATA: 'data_protection.export_data',
  DATA_PROTECTION_MANAGE: 'data_protection.manage',
  DATA_PROTECTION_MANAGE_BACKUPS: 'data_protection.manage_backups',
  DATA_PROTECTION_MANAGE_BREACHES: 'data_protection.manage_breaches',
  DATA_PROTECTION_MANAGE_REQUESTS: 'data_protection.manage_requests',
  DATA_PROTECTION_VIEW: 'data_protection.view',

  // Deposits
  DEPOSITS_FORFEIT: 'deposits.forfeit',
  DEPOSITS_RETURN: 'deposits.return',
  DEPOSITS_VIEW: 'deposits.view',

  // Disputes
  DISPUTES_UPDATE: 'disputes.update',
  DISPUTES_VIEW: 'disputes.view',

  // Email review (HITL)
  EMAIL_REVIEW_CONFIRM: 'email_review.confirm',
  EMAIL_REVIEW_VIEW: 'email_review.view',

  // Event subscriptions (webhooks)
  EVENT_SUBSCRIPTIONS_CREATE: 'event_subscriptions.create',
  EVENT_SUBSCRIPTIONS_DELETE: 'event_subscriptions.delete',
  EVENT_SUBSCRIPTIONS_UPDATE: 'event_subscriptions.update',
  EVENT_SUBSCRIPTIONS_VIEW: 'event_subscriptions.view',

  // Field visits
  FIELD_VISITS_MANAGE: 'field_visits.manage',

  // Gateway transactions
  GATEWAY_TRANSACTIONS_VIEW: 'gateway_transactions.view',

  // Installations
  INSTALLATIONS_UPDATE: 'installations.update',
  INSTALLATIONS_VIEW: 'installations.view',

  // Inventory
  INVENTORY_CONFIRM_PROVIDER_RECEIPT: 'inventory.confirm_provider_receipt',
  INVENTORY_CREATE: 'inventory.create',
  INVENTORY_LEASE: 'inventory.lease',
  INVENTORY_RMA: 'inventory.rma',
  INVENTORY_SELL: 'inventory.sell',
  INVENTORY_TRANSFER: 'inventory.transfer',
  INVENTORY_UPDATE: 'inventory.update',
  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_WRITE_OFF: 'inventory.write_off',

  // Invoices
  INVOICES_CREATE: 'invoices.create',
  INVOICES_DELETE: 'invoices.delete',
  INVOICES_DISCOUNT: 'invoices.discount',
  INVOICES_ISSUE: 'invoices.issue',
  INVOICES_UPDATE: 'invoices.update',
  INVOICES_VIEW: 'invoices.view',

  // Leads (CRM) — W3.1 pre-sale pipeline
  LEADS_ASSIGN: 'leads.assign',
  LEADS_CONVERT: 'leads.convert',
  LEADS_CREATE: 'leads.create',
  LEADS_DELETE: 'leads.delete',
  LEADS_UPDATE: 'leads.update',
  LEADS_VIEW: 'leads.view',

  // LLM usage
  LLM_USAGE_VIEW: 'llm_usage.view',
  LLM_USAGE_VIEW_FULL: 'llm_usage.view_full',

  // Locations
  LOCATIONS_MANAGE: 'locations.manage',

  // Notes
  NOTES_CREATE: 'notes.create',
  NOTES_DELETE: 'notes.delete',
  NOTES_UPDATE: 'notes.update',
  NOTES_VIEW: 'notes.view',

  // Notifications
  NOTIFICATIONS_VIEW: 'notifications.view',

  // Offers (Presupuestos) — non-fiscal quotes, never submitted to AEAT/FD
  OFFERS_CREATE: 'offers.create',
  OFFERS_MANAGE: 'offers.manage',
  OFFERS_VIEW: 'offers.view',

  // Orders
  ORDERS_APPROVE: 'orders.approve',
  ORDERS_BYPASS_WORKFLOW_GATES: 'orders.bypass_workflow_gates',
  ORDERS_CREATE: 'orders.create',
  ORDERS_CREATE_FOR_SUSPENDED: 'orders.create_for_suspended',
  ORDERS_DELETE: 'orders.delete',
  ORDERS_FORCE_ADVANCE: 'orders.force_advance',
  ORDERS_UPDATE: 'orders.update',
  ORDERS_VIEW: 'orders.view',

  // Payments
  PAYMENTS_CREATE: 'payments.create',
  PAYMENTS_REFUND: 'payments.refund',
  PAYMENTS_SYNC: 'payments.sync',
  PAYMENTS_VIEW: 'payments.view',

  // Products
  PRODUCTS_CREATE: 'products.create',
  PRODUCTS_DELETE: 'products.delete',
  PRODUCTS_UPDATE: 'products.update',
  PRODUCTS_VIEW: 'products.view',

  // Provider tickets
  PROVIDER_TICKETS_MANAGE: 'provider_tickets.manage',
  PROVIDER_TICKETS_VIEW: 'provider_tickets.view',

  // Providers
  PROVIDERS_CREATE: 'providers.create',
  PROVIDERS_DELETE: 'providers.delete',
  PROVIDERS_MANAGE_SLA: 'providers.manage_sla',
  PROVIDERS_UPDATE: 'providers.update',
  PROVIDERS_VIEW: 'providers.view',

  // Reports
  REPORTS_EXPORT: 'reports.export',
  REPORTS_VIEW: 'reports.view',

  // Roles
  ROLES_CREATE: 'roles.create',
  ROLES_DELETE: 'roles.delete',
  ROLES_UPDATE: 'roles.update',
  ROLES_VIEW: 'roles.view',

  // Settings
  SETTINGS_DELETE: 'settings.delete',
  SETTINGS_UPDATE: 'settings.update',
  SETTINGS_VIEW: 'settings.view',
  SETTINGS_MANAGE_LANGUAGES: 'settings.manage_languages',

  // Stock
  STOCK_RECEIVE: 'stock.receive',

  // Subscriptions
  SUBSCRIPTIONS_CREATE: 'subscriptions.create',
  SUBSCRIPTIONS_DELETE: 'subscriptions.delete',
  SUBSCRIPTIONS_PAYMENT_GATEWAY_UPDATE: 'subscriptions.payment_gateway.update',
  SUBSCRIPTIONS_UPDATE: 'subscriptions.update',
  SUBSCRIPTIONS_VIEW: 'subscriptions.view',

  // Suppliers
  SUPPLIERS_MANAGE: 'suppliers.manage',

  // Task templates
  TASK_TEMPLATES_MANAGE: 'task_templates.manage',

  // Tasks
  TASKS_ASSIGN: 'tasks.assign',
  TASKS_CREATE: 'tasks.create',
  TASKS_DELETE: 'tasks.delete',
  TASKS_MANAGE_ALL: 'tasks.manage_all',
  TASKS_UPDATE: 'tasks.update',
  TASKS_VIEW: 'tasks.view',

  // Tickets
  TICKETS_CREATE: 'tickets.create',
  TICKETS_DELETE: 'tickets.delete',
  TICKETS_ESCALATE: 'tickets.escalate',
  TICKETS_UPDATE: 'tickets.update',
  TICKETS_VIEW: 'tickets.view',

  // Users
  USERS_CREATE: 'users.create',
  USERS_DELETE: 'users.delete',
  USERS_UPDATE: 'users.update',
  USERS_VIEW: 'users.view',

  // Workflows (workflow-engine redesign — dedicated workflows.* permission set)
  WORKFLOWS_VIEW: 'workflows.view',
  WORKFLOWS_UPDATE: 'workflows.update',
  WORKFLOWS_PUBLISH: 'workflows.publish',
  WORKFLOWS_DELETE: 'workflows.delete',
  WORKFLOWS_ASSIGN: 'workflows.assign',
} as const;

// ---------------------------------------------------------------------------
// Derived types
// ---------------------------------------------------------------------------

/**
 * Union of all valid permission strings.
 * Use this type for props/parameters that accept a single permission string.
 * TypeScript will error at compile time for any string not in the seeder.
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Extract the resource prefix from a permission string.
 * e.g. 'customers.view' → 'customers'
 */
export type PermissionResource = Permission extends `${infer R}.${string}` ? R : never;
